<template>
  <header
    class="project-header"
    ref="projectHeaderElement"
    :style="{ '--scroll-progress': scrollProgress }"
  >
    <div class="content">
      <p ref="linkElement" class="backlink">
        <NuxtLink class="button-back-default" to="/projekte">
          <span class="arrow"></span>
          {{ general?.projectHeaderBackLink }}
        </NuxtLink>
      </p>
      <h1 class="title" ref="titleElement">{{ title }}</h1>
      <p class="service" ref="eyebrowElement" v-if="serviceName">{{ serviceName }}</p>
    </div>
    <DatoAsset
      :lazy-load="false"
      class="asset"
      v-if="image"
      :asset="image"
      :asset-vs="imageSmall"
    />
    <span class="placeholder asset" v-else></span>
  </header>
</template>

<script setup lang="ts">
import useLinesFadeIn from '~/utils/useLinesFadeIn';
import { useWindowSize } from '#imports';
import type { DatoAssetDto } from '~/dato/dato';
import DatoAsset from '~/dato/DatoAsset.vue';
import type { GeneralFragment } from '#gql';

const props = defineProps<{
  title: string;
  serviceName: string;
  image: DatoAssetDto;
  imageSmall?: DatoAssetDto;
}>();

const general = inject<GeneralFragment>('general');

const linkElement = ref<null | HTMLElement>(null);
const eyebrowElement = ref<null | HTMLElement>(null);
const titleElement = ref<null | HTMLElement>(null);
const projectHeaderElement = ref<null | HTMLElement>(null);
const { height: windowHeight } = useWindowSize();
const { y: windowScroll } = useWindowScroll();

useLinesFadeIn([linkElement, titleElement, eyebrowElement], { delay: 0.85 });

const scrollProgress = computed(function () {
  if (!windowHeight.value) {
    return 0;
  }
  return Math.min(1, windowScroll.value / windowHeight.value);
});

onMounted(function () {
  const assetElement = projectHeaderElement.value?.querySelector('.asset');

  if (assetElement instanceof HTMLElement) {
    resizeImageOnWidowResize(assetElement);
  }
});

function resizeImageOnWidowResize(el: HTMLElement) {
  window.addEventListener('resize', onResize);

  function onResize() {
    el.style.width = `${window.innerWidth}px`;
    // el.style.height = `${window.innerHeight}px`;
  }
}
</script>

<style scoped lang="scss">
//.project-header + div {
//  z-index: 10;
//}

.project-header {
  width: 100%;
  height: 100svh;
  color: var(--color-white);
  overflow: clip;
  position: sticky;
  top: 0;

  @media (min-aspect-ratio: 18/10) {
    min-height: unset;
    max-height: 100lvh;
    height: 100lvh;
  }

  > .content {
    > .backlink,
    > .title,
    > .service {
      margin-left: var(--base-component-padding-inline);
      margin-right: var(--base-component-padding-inline);
    }

    > .backlink {
      padding-top: 10rem;
      padding-bottom: 0.5rem;

      @media (--vs) {
        padding-bottom: 1.5rem;
      }
      @media (--vl) {
        padding-bottom: 2.5rem;
      }
    }

    > .title {
      max-width: 17ch;

      @media (--vs) {
        @include text-4xl(700);

        padding-bottom: 0.5rem;
      }

      @media (--vl) {
        @include text-5xl(700);

        padding-bottom: 2.5rem;
      }
    }

    > .service {
      @include text-xl(700);

      padding-bottom: 2rem;
    }
  }

  > .asset {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100svh;
    z-index: -1;

    @media (min-aspect-ratio: 18/10) {
      height: 100lvh;
    }

    :deep(.image) {
      object-fit: cover;
      transform: translateY(calc(var(--scroll-progress) * -100px));
      will-change: transform;
      filter: brightness(var(--brightness, 0.5));
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
    }
  }

  > .placeholder {
    background-color: var(--color-dark-grey);
  }
}
</style>
